import { useRef, useState } from 'react'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, Typography,
} from '@mui/material'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'

import { primary } from '../../../theme/palette'
import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { useCategoriesStore } from './categories'

const VALID_TYPES = ['building', 'floor', 'wing', 'room', 'area', 'zone', 'outdoor', 'other']

const SAMPLE_CSV = `name,type,parent_name,description
Main Building,building,,The primary office building
Floor 1,floor,Main Building,Ground floor
Lobby,room,Floor 1,Main entrance lobby
Break Room,room,Floor 1,Staff break area
Floor 2,floor,Main Building,Upper floor
Server Room,room,Floor 2,IT equipment room
Outdoor Parking,outdoor,,Staff parking area
Loading Dock,area,,Goods receiving area`

interface ImportError {
  row: number
  field: string
  value: string
  message: string
}

interface ParsedRow {
  name: string
  type: string
  parent_name: string
  description: string
  rowNum: number
}

interface Props {
  open: boolean
  onClose: () => void
  siteId: string
}

export function ImportDialog({ open, onClose, siteId }: Props) {
  const qc = useQueryClient()
  const categories = useCategoriesStore((s) => s.categories)
  const fileRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null)
  const [errors, setErrors] = useState<ImportError[]>([])
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState<{ success: number; failed: number } | null>(null)

  function downloadSample() {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'locations_import_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setFile(f)
    setResult(null)
    setErrors([])

    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      const { rows, errors: parseErrors } = parseCSV(text, categories.map((c) => c.id))
      setParsed(rows)
      setErrors(parseErrors)
    }
    reader.readAsText(f)
  }

  async function handleImport() {
    if (!parsed || parsed.length === 0) return
    setImporting(true)

    let success = 0
    let failed = 0
    const createdMap = new Map<string, string>() // name → id

    // Sort by hierarchy depth (parents first) using parent_name
    const sorted = [...parsed].sort((a, b) => {
      if (!a.parent_name && b.parent_name) return -1
      if (a.parent_name && !b.parent_name) return 1
      return a.rowNum - b.rowNum
    })

    for (const row of sorted) {
      try {
        let parentId: string | undefined
        if (row.parent_name) {
          parentId = createdMap.get(row.parent_name.toLowerCase())
          if (!parentId) {
            // Try to find from already-existing locations
            const existing = await api.locations.list(siteId, {}).catch(() => [])
            const match = (Array.isArray(existing) ? existing : []).find(
              (l) => l.name.toLowerCase() === row.parent_name.toLowerCase()
            )
            parentId = match?.id
          }
          if (!parentId) {
            setErrors((prev) => [...prev, { row: row.rowNum, field: 'parent_name', value: row.parent_name, message: `Parent "${row.parent_name}" not found. Create it first or check spelling.` }])
            failed++
            continue
          }
        }

        const created = await api.locations.create(siteId, {
          name: row.name,
          type: row.type as any,
          parent_id: parentId,
          description: row.description || undefined,
        })
        createdMap.set(row.name.toLowerCase(), created.id)
        success++
      } catch (e) {
        failed++
        const msg = e instanceof ApiError ? e.message : 'Unknown error'
        setErrors((prev) => [...prev, { row: row.rowNum, field: 'api', value: row.name, message: msg }])
      }
    }

    setResult({ success, failed })
    setImporting(false)
    if (success > 0) {
      qc.invalidateQueries({ queryKey: ['location-tree', siteId] })
      toast.success(`${success} location${success === 1 ? '' : 's'} imported`)
    }
  }

  function reset() {
    setFile(null)
    setParsed(null)
    setErrors([])
    setResult(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  function handleClose() {
    reset()
    onClose()
  }

  const hasValidationErrors = errors.length > 0 && !result
  const allValid = parsed && parsed.length > 0 && errors.length === 0 && !result

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Import locations</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5, lineHeight: 1.6 }}>
          Upload a CSV file to create multiple locations at once. Download the template to see the expected format.
        </Typography>

        {/* Download template */}
        <Box
          onClick={downloadSample}
          sx={{
            px: 2.5, py: 2, mb: 2.5, borderRadius: 2,
            border: '1px solid', borderColor: 'divider', cursor: 'pointer',
            transition: 'border-color 120ms ease',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FileDownloadOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>Download template CSV</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Columns: name, type, parent_name, description
              </Typography>
            </Box>
            <Chip label=".csv" size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: 'action.hover', color: 'text.secondary' }} />
          </Stack>
        </Box>

        <Divider sx={{ mb: 2.5 }}><Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Upload</Typography></Divider>

        {/* File upload area */}
        <input ref={fileRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />

        {!file ? (
          <Box
            onClick={() => fileRef.current?.click()}
            sx={{
              py: 5, textAlign: 'center', borderRadius: 2,
              border: '2px dashed', borderColor: 'divider', cursor: 'pointer',
              transition: 'border-color 120ms ease, background-color 120ms ease',
              '&:hover': { borderColor: 'primary.main', bgcolor: primary.hover },
            }}
          >
            <FileUploadOutlinedIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.6, mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Click to upload CSV</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>or drag and drop</Typography>
          </Box>
        ) : (
          <Box>
            {/* File info */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', mb: 2 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{file.name}</Typography>
                <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                  {parsed ? `${parsed.length} row${parsed.length === 1 ? '' : 's'} parsed` : 'Parsing…'}
                </Typography>
              </Box>
              <Button size="small" onClick={reset} sx={{ fontSize: 12, color: 'text.secondary' }}>Change</Button>
            </Stack>

            {/* Validation results */}
            {hasValidationErrors && (
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                  <ErrorOutlineIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#DC2626' }}>
                    {errors.length} issue{errors.length === 1 ? '' : 's'} found — fix before importing
                  </Typography>
                </Stack>
                <Box sx={{ maxHeight: 200, overflowY: 'auto', borderRadius: 1.5, border: '1px solid', borderColor: 'rgba(220,38,38,0.2)', bgcolor: 'rgba(220,38,38,0.04)' }}>
                  {errors.map((err, i) => (
                    <Stack key={i} direction="row" spacing={1.5} sx={{ px: 2, py: 1.25, borderBottom: i < errors.length - 1 ? '1px solid' : 'none', borderColor: 'rgba(220,38,38,0.12)' }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#DC2626', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        Row {err.row}
                      </Typography>
                      <Box>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                          {err.field}: <Typography component="span" sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>"{err.value}"</Typography>
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{err.message}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Box>
              </Box>
            )}

            {/* All valid */}
            {allValid && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, borderRadius: 1.5, bgcolor: 'rgba(5,150,105,0.06)', border: '1px solid', borderColor: 'rgba(5,150,105,0.2)', mb: 2 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#059669' }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>
                  {parsed.length} location{parsed.length === 1 ? '' : 's'} ready to import
                </Typography>
              </Stack>
            )}

            {/* Import result */}
            {result && (
              <Box sx={{ mb: 2 }}>
                <Stack spacing={1}>
                  {result.success > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderRadius: 1.5, bgcolor: 'rgba(5,150,105,0.06)', border: '1px solid', borderColor: 'rgba(5,150,105,0.2)' }}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#059669' }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#059669' }}>{result.success} imported successfully</Typography>
                    </Stack>
                  )}
                  {result.failed > 0 && (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderRadius: 1.5, bgcolor: 'rgba(220,38,38,0.06)', border: '1px solid', borderColor: 'rgba(220,38,38,0.2)' }}>
                      <ErrorOutlineIcon sx={{ fontSize: 16, color: '#DC2626' }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#DC2626' }}>{result.failed} failed — see errors above</Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* Format guide */}
        <Box sx={{ mt: 2.5, px: 2.5, py: 2, borderRadius: 2, bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFB' : 'rgba(255,255,255,0.02)', border: '1px solid', borderColor: 'divider' }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>
            CSV format
          </Typography>
          <Stack spacing={0.75}>
            <FormatRow field="name" required desc="Location name (required)" />
            <FormatRow field="type" required desc={`One of: ${VALID_TYPES.join(', ')}`} />
            <FormatRow field="parent_name" desc="Name of the parent location (empty = root)" />
            <FormatRow field="description" desc="Optional description" />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} sx={{ fontSize: 13, color: 'text.secondary' }}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button variant="contained"
            onClick={handleImport}
            disabled={!allValid || importing}
            startIcon={importing ? <CircularProgress size={14} /> : <FileUploadOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{ fontSize: 13, fontWeight: 600 }}>
            {importing ? 'Importing…' : `Import ${parsed?.length ?? 0} location${(parsed?.length ?? 0) === 1 ? '' : 's'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

function FormatRow({ field, required, desc }: { field: string; required?: boolean; desc: string }) {
  return (
    <Stack direction="row" alignItems="baseline" spacing={1}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: 'text.primary', minWidth: 100 }}>
        {field}{required && <Typography component="span" sx={{ color: '#DC2626' }}>*</Typography>}
      </Typography>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{desc}</Typography>
    </Stack>
  )
}

// ─── CSV parser with validation ─────────────────────────────────────────────

function parseCSV(text: string, validTypes: string[]): { rows: ParsedRow[]; errors: ImportError[] } {
  const lines = text.trim().split('\n').map((l) => l.trim()).filter(Boolean)
  if (lines.length < 2) return { rows: [], errors: [{ row: 1, field: 'file', value: '', message: 'CSV must have a header row and at least one data row.' }] }

  const header = lines[0]!.split(',').map((h) => h.trim().toLowerCase().replace(/^"/, '').replace(/"$/, ''))
  const nameIdx = header.indexOf('name')
  const typeIdx = header.indexOf('type')
  const parentIdx = header.indexOf('parent_name')
  const descIdx = header.indexOf('description')

  const errors: ImportError[] = []

  if (nameIdx < 0) errors.push({ row: 1, field: 'header', value: header.join(', '), message: 'Missing required column "name".' })
  if (typeIdx < 0) errors.push({ row: 1, field: 'header', value: header.join(', '), message: 'Missing required column "type".' })
  if (errors.length > 0) return { rows: [], errors }

  const allValidTypes = [...VALID_TYPES, ...validTypes]

  const rows: ParsedRow[] = []
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i]!)
    const name = cols[nameIdx]?.trim() ?? ''
    const type = cols[typeIdx]?.trim().toLowerCase() ?? ''
    const parentName = parentIdx >= 0 ? (cols[parentIdx]?.trim() ?? '') : ''
    const description = descIdx >= 0 ? (cols[descIdx]?.trim() ?? '') : ''

    if (!name) {
      errors.push({ row: i + 1, field: 'name', value: '', message: 'Name is required.' })
      continue
    }
    if (!type) {
      errors.push({ row: i + 1, field: 'type', value: '', message: 'Type is required.' })
      continue
    }
    if (!allValidTypes.includes(type)) {
      errors.push({ row: i + 1, field: 'type', value: type, message: `Invalid type "${type}". Must be one of: ${VALID_TYPES.join(', ')}` })
      continue
    }

    rows.push({ name, type, parent_name: parentName, description, rowNum: i + 1 })
  }

  return { rows, errors }
}

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') { inQuotes = !inQuotes; continue }
    if (ch === ',' && !inQuotes) { result.push(current); current = ''; continue }
    current += ch
  }
  result.push(current)
  return result
}
